import {
  getSelectedProduct,
  selectGroup,
  getSelectedGroupId,
  getProductGroups
} from 'ducks/products';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useCore } from 'contexts/core';
import { KompisActionList, KompisActionListItem } from '@inter-ikea-kompis/react-components';
import { usePlannerConfig } from 'contexts/plannerConfig';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { ActionListControlEnum } from '@inter-ikea-kompis/component-action-list/lib/';
import styled from 'styled-components';
import { isMobile } from 'utils/platform';
import PillGroupCarousel from 'ui/PillGroupCarousel/PillGroupCarousel';
import { setInvalidSlidingDoorPlacementInfo } from 'ducks/layout';
import { getQuery, rangeIsBesta, rangeIsBror, rangeIsSmastad } from 'utils/queryUtils';
import { useAppDispatch } from 'ducks/hooks';
import SkipButton from 'ui/SkipButton/SkipButton';
import BestaRangeToggle from 'rangeConfig/besta/widgets/RangeToggle';
import BrorRangeToggle from 'rangeConfig/bror/widgets/RangeToggle';
import { getHiddenProductGroups } from 'ducks/range';
import AgeGroupSwitcher from 'components/AgeGroupSwitcher/AgeGroupSwitcher';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useServiceSettings } from 'contexts/serviceSettings';
import Search, { SearchState } from '@ingka/search';
import SearchResult from './SearchResult';

const MobileProductGroupNav = styled.div`
  display: flex;
  align-items: center;
  width: 100vw;
`;

const MobileWrapper = styled.div`
  height: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  height: 100%;
`;

const SearchWrapper = styled.div`
  position: sticky;
  background: white;
  top: 0;
  z-index: 100;
  padding: ${({ theme }) => `${theme.space.s} ${theme.space.xl}`};
`;

const HeaderWrapper = styled.div`
  position: sticky;
  padding: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.xxl}
    ${({ theme }) => theme.space.s} ${({ theme }) => theme.space.xxl};
`;

const ActionListWrapper = styled.div`
  position: relative;
  overflow-y: auto;
  height: 100%;
  padding: ${({ theme }) => `${theme.space.s} 1.3rem ${theme.space.xl}`};
  kompis-action-list-item {
    :first-child {
      border-top: none;
    }
    :last-child {
      border-bottom: none;
    }
  }
`;

type Props = {
  onGroupClicked?: () => void;
};

const ProductGroupNav = ({ onGroupClicked }: Props) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const hiddenProductGroups = useSelector(getHiddenProductGroups);
  const behaviorTracker = useBehaviorTracker();
  const { enableKeyboardNavigation } = useServiceSettings().dexfSettings.general;
  const [searchString, setSearchString] = React.useState<string>('');
  const enableSearch = getQuery().get('enableSearch') === 'true';

  // Planner config
  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const productsInProductGroups = useSelector(getProductGroups);

  const selectedProduct = useSelector(getSelectedProduct);
  const selectedGroupId = useSelector(getSelectedGroupId);

  const onClick = useCallback(
    (groupId: string) => {
      const productGroup = productGroups.find(i => i.id === groupId);
      behaviorTracker
        .withContext({ groupId, menuType: 'main' })
        .trigger('productGroupEntered', null);

      // select group if not already selected
      if (productGroup && productGroup.id !== selectedGroupId) {
        dispatch(selectGroup(productGroup.id));
        dispatch(setInvalidSlidingDoorPlacementInfo(false));
      }

      if (selectedProduct) {
        coreApi.entities.unselect();
      }

      onGroupClicked && productGroup && onGroupClicked();
    },
    [
      behaviorTracker,
      coreApi,
      dispatch,
      onGroupClicked,
      productGroups,
      selectedGroupId,
      selectedProduct
    ]
  );

  const visibleProductGroups = productGroups.filter(group => {
    if (hiddenProductGroups.includes(group.id)) {
      return false;
    } else if (
      group.productTypes.length > 0 &&
      productsInProductGroups[group.id] &&
      productsInProductGroups[group.id].all.length === 0
    ) {
      // If there are no products in the group, we hide it.
      return false;
    }
    return true;
  });

  useEffect(() => {
    const menuItems = productGroups?.length;
    if (menuItems === 1 && !selectedProduct) {
      onClick(productGroups[0].id);
    }
  }, [productGroups, selectedProduct, onClick]);

  let RangeToggle = null;
  if (rangeIsBesta()) RangeToggle = BestaRangeToggle;
  else if (rangeIsBror()) RangeToggle = BrorRangeToggle;

  if (isMobile()) {
    const groups = visibleProductGroups.map(group => ({
      id: group.id,
      name: formatMessage({ id: group.mainMenu.menuTitle }),
      icon: group.icon
    }));
    return (
      <MobileWrapper>
        {RangeToggle ? (
          <RangeToggle />
        ) : (
          <Title p="s" type={ThemeFontStyleTypeEnum.headingXSmall}>
            {formatMessage({
              id: 'storageone.newMenuHeading'
            })}
          </Title>
        )}
        <MobileProductGroupNav>
          <PillGroupCarousel
            data-testid="mobile-group-carousel"
            onSelect={onClick}
            items={groups}
            px="s"
            hideIcons
          />
        </MobileProductGroupNav>
      </MobileWrapper>
    );
  }

  const onSearch = (_e: unknown, state: SearchState) => {
    if (state.value === '') {
      setSearchString('');
      clearTimeout((onSearch as any).timeout);
      return;
    }
    clearTimeout((onSearch as any).timeout);
    (onSearch as any).timeout = setTimeout(() => {
      setSearchString(state.value || '');
    }, 300);
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        {enableKeyboardNavigation && (
          <SkipButton
            dataTestId="skip-button-product-list-top"
            skipToId="scene-section"
            text={formatMessage({ id: 'storageone.skipButtonProductListTop' })}
          />
        )}
        <Title display="flex" alignItems="center" type={ThemeFontStyleTypeEnum.headingMedium}>
          {formatMessage({
            id: 'storageone.newMenuHeading'
          })}
        </Title>
      </HeaderWrapper>
      {enableSearch && (
        <>
          <SearchWrapper>
            <Search
              id="search"
              onSearch={onSearch}
              onChange={onSearch}
              onClear={() => setSearchString('')}
            />
          </SearchWrapper>
          {searchString !== '' && <SearchResult searchString={searchString} />}
        </>
      )}
      {searchString === '' && (
        <ActionListWrapper>
          <KompisActionList>
            {RangeToggle && <RangeToggle />}
            {rangeIsSmastad() && <AgeGroupSwitcher />}
            {visibleProductGroups
              .filter(({ mainMenu }) => {
                return mainMenu.hidden ? !mainMenu.hidden(coreApi) : true;
              })
              .map(group => {
                return (
                  <KompisActionListItem
                    data-testid={group.id}
                    key={group.id}
                    theme={SkapaTheme}
                    label={formatMessage({
                      id: group.mainMenu.menuTitle
                    })}
                    leadingIcon={group.icon}
                    onSelect={() => onClick(group.id)}
                    control={ActionListControlEnum.chevron}
                    horizontalPadding={true}
                  />
                );
              })}
          </KompisActionList>
        </ActionListWrapper>
      )}
    </Wrapper>
  );
};

export default ProductGroupNav;
